import { useCallback, useState } from "react";
import { toast } from "sonner";
import { PlaybookToast } from "@/components/playbook/PlaybookToast";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { queueCheckin, isNetworkFailure } from "@/lib/offline";
import { DAILY_CHALLENGES } from "@/data/checkin";

const INITIAL = {
  mood: 5, energy: 5, sleepHours: 7, sleepQuality: 3, anxiety: 0, symptom: 0, challengeLevel: 0,
  activities: [], triggers: [], struggles: [], tags: [], journal: "", done: [], gratitude: "",
};

const toPayload = (f) => ({
  mood_score: f.mood, energy_level: f.energy, sleep_hours: f.sleepHours, sleep_quality: f.sleepQuality,
  anxiety_level: f.anxiety, stress_level: f.anxiety, symptom_intensity: f.symptom, challenge_level: f.challengeLevel,
  activities: f.activities, habits_completed: f.activities, triggers: f.triggers, mood_tags: f.tags,
  journal_entry: f.journal || null,
  challenges: [
    ...f.struggles.map((s) => ({ type: "struggle", name: s })),
    ...f.done.map((id) => ({ type: "daily_challenge", id, points: DAILY_CHALLENGES.find((c) => c.id === id)?.points || 0 })),
  ],
  gratitude: f.gratitude ? [f.gratitude] : null,
  timestamp: new Date().toISOString(),
});

export function useCheckInForm(onDone) {
  const nav = useNavigate();
  const [f, setF] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const set = useCallback((key) => (value) => setF((prev) => ({ ...prev, [key]: value })), []);

  const submit = async () => {
    const body = toPayload(f);
    const saveOffline = () => {
      queueCheckin(body);
      toast.success("Saved offline 📴 It will sync when you're back online.");
      onDone();
    };
    if (!navigator.onLine) return saveOffline();
    setBusy(true);
    try {
      const { data } = await api.post("/checkins", body);
      toast.success("Check-in complete! 🎉 A new flower was planted in your garden 🌸");
      if (data?.open_when) toast("💌 You wrote yourself a letter for this", { description: `Open when… ${data.open_when.topic}`, duration: 10000,
        action: { label: "Read it", onClick: () => nav(`/letters?ow=${data.open_when.id}`) } });
      if (data?.playbook_helpers?.length) toast.custom((id) => <PlaybookToast helpers={data.playbook_helpers} onClose={() => toast.dismiss(id)} />, { duration: 15000 });
      if (data?.letter_delivered) toast("💌 A letter from your past self has arrived", { description: "It's waiting for you on Home.", duration: 8000 });
      onDone();
    } catch (e) {
      if (isNetworkFailure(e)) saveOffline();
      else toast.error(errMsg(e, "Failed to save check-in"));
    } finally {
      setBusy(false);
    }
  };

  return { f, set, busy, submit };
}
